import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { AppContext } from "../AppContext";
import toast from "react-hot-toast";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { authFetch } from "../learnerApi";

const LoginPage = () => {
  const { login, setUsername, setIsLoggedIn, API_BASE_URL } =
    useContext(AppContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const gateMessage = location.state?.message;
  const auth = getAuth();

  // Đăng nhập Google
  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const response = await authFetch(`${API_BASE_URL}/users/${user.uid}`);
      if (response.ok) {
        const existingUser = await response.json();
        setUsername(existingUser.displayName || existingUser.email);
        setIsLoggedIn(true);
        toast.success("Đăng nhập thành công!");
        navigate(redirectTo, { replace: true });
      } else if (response.status === 404) {
        const createUser = await authFetch(`${API_BASE_URL}/users/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email,
            emailVerified: user.emailVerified,
            currentCoin: 100,
          }),
        });

        if (createUser.ok) {
          const newUser = await createUser.json();
          setUsername(newUser.displayName || newUser.email);
          setIsLoggedIn(true);
          toast.success("Đăng nhập thành công!");
          navigate(redirectTo, { replace: true });
        } else {
          setError("Tạo người dùng thất bại.");
        }
      } else {
        setError("Lỗi không xác định khi xác minh người dùng.");
      }
    } catch (err) {
      setError("Đăng nhập bằng Google thất bại: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Đăng nhập Email/Password
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setEmailNotVerified(false);
    setLoading(true);

    try {
      const userData = await login(email, password);

      if (userData instanceof Error) {
        let errorMessage = userData.message;

        if (userData.code === "auth/wrong-password") {
          errorMessage = "Mật khẩu không đúng!";
        } else if (userData.code === "auth/user-not-found") {
          errorMessage = "Email không tồn tại.";
        } else if (userData.code === "auth/invalid-credential") {
          errorMessage = "Thông tin đăng nhập không chính xác!";
        } else if (userData.code === "auth/too-many-requests") {
          errorMessage =
            "Đăng nhập thất bại do quá nhiều yêu cầu. Vui lòng thử lại sau.";
        } else if (
          errorMessage === "Vui lòng xác minh email trước khi đăng nhập."
        ) {
          setEmailNotVerified(true);
          errorMessage = null;
        }

        if (errorMessage) setError(errorMessage);
        return;
      }

      if (!userData || !userData.uid) {
        setError("Đăng nhập thất bại: UID không hợp lệ.");
        return;
      }

      const response = await authFetch(`${API_BASE_URL}/users/${userData.uid}`);
      if (response.ok) {
        const existingUser = await response.json();
        setUsername(existingUser.displayName || existingUser.email);
        setIsLoggedIn(true);
        toast.success("Đăng nhập thành công!");
        navigate(redirectTo, { replace: true });
      } else if (response.status === 404) {
        const createUser = await authFetch(`${API_BASE_URL}/users/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: userData.uid,
            email: userData.email,
            displayName: userData.displayName || userData.email,
            emailVerified: userData.emailVerified,
            currentCoin: 100,
          }),
        });

        if (createUser.ok) {
          const newUser = await createUser.json();
          setUsername(newUser.displayName || newUser.email);
          setIsLoggedIn(true);
          toast.success("Đăng nhập thành công!");
          navigate(redirectTo, { replace: true });
        } else {
          setError("Tạo người dùng thất bại.");
        }
      } else {
        setError("Lỗi không xác định khi xác minh người dùng.");
      }
    } catch (err) {
      setError("Lỗi không xác định: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-160px)] bg-gray-100 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md animate-fade-in-down">
        <h2 className="text-4xl font-extrabold text-gray-900 text-center mb-8">
          Đăng Nhập
        </h2>

        {/* Banner yêu cầu đăng nhập nếu điều hướng từ QuizPage */}
        {gateMessage && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Thông báo!</strong> {gateMessage}
          </div>
        )}

        {emailNotVerified && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Thông báo!</strong> Vui lòng xác minh
            email trước khi đăng nhập.
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Lỗi!</strong> {error}
          </div>
        )}

        {/* Form Email */}
        <form onSubmit={handleLogin}>
          <div className="mb-6">
            <label
              htmlFor="email"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Email
            </label>
            <div className="relative">
              <Mail
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="w-full py-3 px-10 rounded-xl border shadow focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập email"
              />
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Mật khẩu
            </label>
            <div className="relative">
              <Lock
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full py-3 px-10 rounded-xl border shadow focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập mật khẩu"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Đang đăng nhập...</span>
              </>
            ) : (
              "Đăng Nhập"
            )}
          </button>
        </form>

        {/* Separator */}
        <div className="flex items-center my-6">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="mx-4 text-gray-500 text-sm">Hoặc</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        {/* Google Login */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="30"
            height="30"
            viewBox="0 0 48 48"
          >
            <path
              fill="#FFC107"
              d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
            ></path>
            <path
              fill="#FF3D00"
              d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
            ></path>
            <path
              fill="#4CAF50"
              d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
            ></path>
            <path
              fill="#1976D2"
              d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
            ></path>
          </svg>
          Đăng nhập với Google
        </button>

        {/* Link đăng ký */}
        <button
          onClick={() => navigate("/register", { state: { from: redirectTo } })}
          disabled={loading}
          className="text-blue-600 hover:underline mt-6 text-sm w-full text-center"
        >
          Chưa có tài khoản? Đăng ký ngay
        </button>
      </div>

      <style>
        {`
          @keyframes fadeInDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in-down {
            animation: fadeInDown 0.5s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

export default LoginPage;
